
import { Injectable, ErrorHandler, Injector } from '@angular/core';
import { TelemetryService } from './telemetry/telemetry.service';
import { LoggingService } from './logging/logging.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorTrackingService implements ErrorHandler {
  private isMobileDevice: boolean;
  private telemetryService: TelemetryService | null = null;

  constructor(
    private injector: Injector,
    private logger: LoggingService
  ) {
    // Detect if user is on a mobile device
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('[ErrorTrackingService] Initialized with service name: finderwise-ui');
  }

  /**
   * Lazily get TelemetryService to avoid circular dependency during bootstrap
   */
  private getTelemetryService(): TelemetryService | null {
    if (!this.telemetryService) {
      try {
        this.telemetryService = this.injector.get(TelemetryService);
      } catch (error) {
        // TelemetryService not available yet - that's ok during early bootstrap
        return null;
      }
    }
    return this.telemetryService;
  }

  /**
   * Handle global unhandled errors
   */
  handleError(error: any): void {
    this.trackError('unhandled_error', error);
  }

  /**
   * Track a specific error
   * @param errorType Type/category of the error
   * @param error The error object or message
   * @param context Additional context information
   */
  trackError(errorType: string, error: any, context: Record<string, any> = {}): void {
    let errorMessage: string;
    let errorStack: string | undefined;
    
    // Extract error details
    if (error instanceof Error) {
      errorMessage = error.message;
      errorStack = error.stack;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else {
      try {
        errorMessage = JSON.stringify(error);
      } catch {
        errorMessage = 'Unknown error';
      }
    }

    // Build complete error context
    const errorContext = {
      ...context,
      errorMessage,
      errorStack,
      timestamp: Date.now(),
      isMobileDevice: this.isMobileDevice,
      url: window.location.href,
      userAgent: navigator.userAgent,
      // Include memory info if available (Chrome only)
      memoryInfo: (performance as any).memory ? {
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize
      } : undefined
    };

    // Track the error in telemetry (lazy injection)
    const telemetry = this.getTelemetryService();
    if (telemetry) {
      telemetry.trackError(`error_${errorType}`, errorContext);
    }
    
    // Log to console
    this.logger.error('ErrorTrackingService', `Error tracked: ${errorType}`, {
      error,
      context: errorContext
    });
  }

  /**
   * Track network errors specifically
   */
  trackNetworkError(url: string, status: number, statusText: string, additionalInfo: Record<string, any> = {}): void {
    const context = {
      url,
      status,
      statusText,
      ...additionalInfo
    };
    
    this.trackError('network', `${status} ${statusText} - ${url}`, context);
  }

  /**
   * Track WebSocket errors specifically
   * @param type The type of WebSocket error
   * @param message Error message
   * @param additionalInfo Additional error context
   */
  trackWebSocketError(type: string, message: string, additionalInfo: Record<string, any> = {}): void {
    const context = {
      type,
      ...additionalInfo
    };
    
    this.trackError('websocket', message, context);
  }
}